import type { VoidFunctionComponent } from 'react';
import PopperMenu from '../../../generic/PopperMenu';
import { useDeleteTeam } from '../../../../hooks/api';
import { useOnConfirm } from '../../../../contexts/DialogContext';
import { useRouter } from '../../../../contexts/RouterContext';
import { useAddToast } from '../../../../contexts/ToastContext';
import { getErrorMessage } from '../../../../ReactQuery';
import { useHasPermissionForTeam } from '../../../../helpers/teams';
import { Action } from '../../../../shared/permissions';

type Props = {
  teamId: number;
  teamName: string;
  onEditTeam: (teamId: number) => void;
};

/**
 * Actions which appear on each row in the teams table,
 * allowing the user to edit, delete, or view the team.
 *
 * @param {Props} props - The props for the component.
 * @param {number} props.teamId - The ID of the team.
 * @param {() => void} props.onEditTeam - The function to call when the user clicks the edit button.
 * @returns {JSX.Element} The component.
 */
const TeamsTableRowActions: VoidFunctionComponent<Props> = ({
  teamId,
  teamName,
  onEditTeam,
}) => {
  const router = useRouter();
  const onConfirm = useOnConfirm();
  const addToast = useAddToast();
  const hasEditPermission = useHasPermissionForTeam(Action.Edit, teamId);
  const hasDeletePermission = useHasPermissionForTeam(Action.Delete, teamId);
  const { mutateAsync: deleteTeam, isLoading: isDeleting } = useDeleteTeam(teamId);

  //  Function to delete a team, and handle any errors
  const onDelete = async () => {
    try {
      await deleteTeam();
      addToast({
        type: 'success',
        content: `Team "${teamName}" deleted.`,
      });
    } catch (error: unknown) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, `Failed to delete team "${teamName}".`),
      });
    }
  };

  //  Wraps the above function in a confirm dialog
  const handleDelete = () =>
    onConfirm({
      title: `Delete "${teamName}"?`,
      body: `Users who are not part of any other team may no longer be able to access any projects.`,
      onConfirm: onDelete,
      confirmText: 'Delete',
      confirmBtnType: 'error',
    });

  return (
    <PopperMenu>
      <PopperMenu.Item
        onClick={() => onEditTeam(teamId)}
        isDisabled={!hasEditPermission || isDeleting}
      >
        Edit team
      </PopperMenu.Item>
      <PopperMenu.Item
        onClick={() => router.push(`/account/settings/organization/teams/${teamId}`)}
        isDisabled={!hasEditPermission || isDeleting}
      >
        Edit members &amp; projects
      </PopperMenu.Item>
      <PopperMenu.Item
        onClick={handleDelete}
        isDisabled={!hasDeletePermission || isDeleting}
        isWarning
      >
        Remove team
      </PopperMenu.Item>
    </PopperMenu>
  );
};

export default TeamsTableRowActions;
